import toast from "react-hot-toast";
import { useEffect } from "react";
import { useMirrorPresenceStatus } from "@/app/hooks/useMirrorPresenceStatus";
import { useUpdateMirrorStateAPI } from "@/app/hooks/api_hooks/Cart/useUpdateMirrorStateAPI";

interface UseCartMirrorPresentationParams {
  userEmailId: string | undefined;
  activeCartId: string;
  customerId: string;
  customerDetails: any;
  activeCartDetails: any;
  mappedClinicId: string;
  petDetails: any;
  fetchCustomerAPI: (customerId: string) => Promise<any>;
  fetchMemberships: (activeCartId: string) => Promise<any>;
  fetchPitchDetails: (activeCartId: string) => Promise<any>;
  isMirrorActionVisible: boolean;
  setIsMirrorActionVisible: (value: boolean) => void;
  setRecentEMRData: (value: any) => void;
  setMembershipData: (value: any) => void;
  setPitchingData: (value: any) => void;
  initialRender: boolean;
  setInitialRender: (value: boolean) => void;
  initialCartLoad: boolean;
  setInitialCartLoad: (value: boolean) => void;
}

// Groups the screen-mirroring "present to customer" flow: the Firestore doc that mirrors the
// active cart, presenting/clearing it on focus/unload/checkout, and the mirror-overlay open state.
export const useCartMirrorPresentation = ({
  userEmailId,
  activeCartId,
  customerId,
  customerDetails,
  activeCartDetails,
  mappedClinicId,
  petDetails,
  fetchCustomerAPI,
  fetchMemberships,
  fetchPitchDetails,
  isMirrorActionVisible,
  setIsMirrorActionVisible,
  setRecentEMRData,
  setMembershipData,
  setPitchingData,
  initialRender,
  setInitialRender,
  initialCartLoad,
  setInitialCartLoad,
}: UseCartMirrorPresentationParams) => {
  const { execute: updateMirrorState } = useUpdateMirrorStateAPI();

  const buildMirrorScreenPayload = () => ({
    screen: "cart_view",
    cart_id: activeCartId ?? null,
    customer_id: customerId ?? null,
    user_name: customerDetails?.name ?? "Guest",
    cart_level_coupon_id: activeCartDetails?.cart_level_coupon_id ?? null,
    clinic_id: mappedClinicId ?? null,
    updated_at: Date.now(),
  });

  const handlePresent = async (isPresent: string) => {
    localStorage.setItem("lastPresentedCartId", activeCartId);
    if (isPresent === "CHECKOUT") {
      await updateMirrorState({ screen: "reminder_view", cart_id: null, status: "CHECKED_OUT" });
      return;
    }
    await updateMirrorState({ ...buildMirrorScreenPayload(), status: "ACTIVE" });
  };

  useEffect(() => {
    const handleFocus = () => {
      if (!activeCartId) return;
      const lastCartId = localStorage.getItem("lastPresentedCartId");
      if (lastCartId !== activeCartId) {
        setIsMirrorActionVisible(false);
        handlePresent("PRESENT");
        localStorage.setItem("lastPresentedCartId", activeCartId);
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [activeCartId]);

  useEffect(() => {
    const handleUnload = () => {
      handlePresent("CHECKOUT");
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    if (
      activeCartId &&
      !initialCartLoad &&
      userEmailId &&
      customerDetails?.name &&
      mappedClinicId &&
      activeCartDetails &&
      customerId
    ) {
      updateMirrorState({ ...buildMirrorScreenPayload(), status: "ACTIVE" });
      toast.success(`Presenting billing details for ${customerDetails?.name}`);
      setInitialCartLoad(true);
    }
  }, [activeCartId, initialCartLoad, userEmailId, customerDetails?.name, mappedClinicId, activeCartDetails, customerId]);

  const openMirrorScreen = () => {
    setIsMirrorActionVisible(true);
    fetchCustomerAPI(customerId).then(({ status, data }) => {
      if (status) {
        setRecentEMRData(data);
      }
    });
    fetchMemberships(activeCartId).then(({ status, data }) => {
      if (status) {
        setMembershipData(data);
      }
    });
    fetchPitchDetails(activeCartId).then(({ status, data }) => {
      if (status) {
        setPitchingData(data);
      }
    });
  };

  const closeMirrorScreen = () => {
    setIsMirrorActionVisible(false);
  };

  useEffect(() => {
    if (petDetails?.high_risk_patient === "High Care" && !initialRender) {
      setIsMirrorActionVisible(true);
      setInitialRender(true);
    }
  }, [petDetails]);

  useEffect(() => {
    if (!customerId) return;
    fetchCustomerAPI(customerId).then(({ status, data }) => {
      if (status) {
        setRecentEMRData(data);
      }
    });
  }, [customerId]);

  useEffect(() => {
    if (isMirrorActionVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMirrorActionVisible]);

  const { presenting: isPresentActive } = useMirrorPresenceStatus();

  return { handlePresent, openMirrorScreen, closeMirrorScreen, isPresentActive };
};
